import { z } from 'zod'

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20

export const UsernameSchema = z
	.string({ required_error: 'Username is required' })
	.min(USERNAME_MIN_LENGTH, { message: 'Username is too short' })
	.max(USERNAME_MAX_LENGTH, { message: 'Username is too long' })
	.regex(/^[a-zA-Z0-9_]+$/, {
		message: 'Username can only include letters, numbers, and underscores',
	})
	// users can type the username in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordSchema = z
	.string({ required_error: 'Password is required' })
	.min(6, { message: 'Password is too short' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'Password is too long',
	})

export const NameSchema = z
	.string({ required_error: 'Name is required' })
	.min(3, { message: 'Name is too short' })
	.max(40, { message: 'Name is too long' })

export const EmailSchema = z
	.string({ required_error: 'Email is required' })
	.email({ message: 'Email is invalid' })
	.min(3, { message: 'Email is too short' })
	.max(100, { message: 'Email is too long' })
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: PasswordSchema, confirmPassword: PasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})

// =============================================================================
// ORGANIZATION VALIDATION
// =============================================================================

export const ORGANIZATION_NAME_MIN_LENGTH = 2
export const ORGANIZATION_NAME_MAX_LENGTH = 100

export const OrganizationNameSchema = z
	.string({ required_error: "Le nom de l'organisation est requis" })
	.min(ORGANIZATION_NAME_MIN_LENGTH, {
		message: "Le nom de l'organisation est trop court",
	})
	.max(ORGANIZATION_NAME_MAX_LENGTH, {
		message: "Le nom de l'organisation est trop long",
	})
	.trim()

// Ordered alphabetically by display label (French). "AUTRE" is intentionally
// kept at the end so the catch-all sits below all named sectors.
export const IndustrySectorValues = [
	'AGRICULTURE_AGROALIMENTAIRE',
	'COMMERCE_DETAIL',
	'COMMUNICATIONS_RP',
	'COMPTABILITE_FINANCE',
	'EDUCATION',
	'ENERGIE_ENVIRONNEMENT',
	'GOUVERNEMENT',
	'IMMOBILIER',
	'MANUFACTURIER',
	'ORDRE_PROFESSIONNEL',
	'OSBL_ASSOCIATION',
	'SANTE',
	'JURIDIQUE',
	'TECHNOLOGIE',
	'TOURISME_HOTELLERIE',
	'AUTRE',
] as const

export const IndustrySectorSchema = z.enum(IndustrySectorValues, {
	required_error: "Le secteur d'activité est requis",
})

export type IndustrySector = z.infer<typeof IndustrySectorSchema>

export const IndustrySectorLabels: Record<IndustrySector, string> = {
	AGRICULTURE_AGROALIMENTAIRE: 'Agriculture et agroalimentaire',
	COMMERCE_DETAIL: 'Commerce de détail',
	COMMUNICATIONS_RP: 'Communications et relations publiques',
	COMPTABILITE_FINANCE: 'Comptabilité et finance',
	EDUCATION: 'Éducation et formation',
	ENERGIE_ENVIRONNEMENT: 'Énergie et environnement',
	GOUVERNEMENT: 'Gouvernement et secteur public',
	IMMOBILIER: 'Immobilier et construction',
	MANUFACTURIER: 'Manufacturier et industriel',
	ORDRE_PROFESSIONNEL: 'Ordre professionnel',
	OSBL_ASSOCIATION: 'OSBL et associations',
	SANTE: 'Santé et services sociaux',
	JURIDIQUE: 'Services juridiques',
	TECHNOLOGIE: 'Technologies et startups',
	TOURISME_HOTELLERIE: 'Tourisme et hôtellerie',
	AUTRE: 'Autre',
}

export const TeamSizeValues = [
	'1',
	'2-5',
	'6-10',
	'11-50',
	'51-200',
	'200+',
] as const

export const TeamSizeSchema = z.enum(TeamSizeValues, {
	required_error: "La taille de l'équipe est requise",
})

export type TeamSize = z.infer<typeof TeamSizeSchema>

export const TeamSizeLabels: Record<TeamSize, string> = {
	'1': '1 personne (solo)',
	'2-5': '2-5 personnes',
	'6-10': '6-10 personnes',
	'11-50': '11-50 personnes',
	'51-200': '51-200 personnes',
	'200+': 'Plus de 200 personnes',
}

// TODO: Add more languages (es, pt, de, it, etc.) — update schema enum + NewsAPI.ai lang mapping
export const LanguageOptions = [
	{ value: 'fr', label: 'Français' },
	{ value: 'en', label: 'Anglais' },
] as const

export const DefaultLanguagesSchema = z
	.preprocess(
		(val) => (typeof val === 'string' ? [val] : val),
		z.array(z.enum(['fr', 'en'])),
	)
	.refine((arr) => arr.length > 0, {
		message: 'Sélectionnez au moins une langue',
	})

export const CompanyStepSchema = z.object({
	organizationName: OrganizationNameSchema,
	industrySector: IndustrySectorSchema,
	teamSize: TeamSizeSchema,
	defaultLanguages: DefaultLanguagesSchema,
})
